/**
 * useVerificationFlow - Reusable hook for verification flows
 *
 * Handles verification state management for device or user verification
 * Works with any Matrix verification request object
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import logger from '../utils/logger';
import { trustEventEmitter, TrustEvents } from '../crypto/events';
import { getVerificationErrorMessage } from '../utils/verificationMessages';
import { useI18n } from '../context/I18nContext';

// Verification flow states
export const VerificationFlowState = {
  IDLE: 'idle',
  ACCEPTING: 'accepting',
  WAITING: 'waiting',
  SHOWING_SAS: 'showing_sas',
  VERIFYING: 'verifying',
  DONE: 'done',
  CANCELLED: 'cancelled',
  ERROR: 'error',
};

/**
 * useVerificationFlow hook
 *
 * @param {Object} request - Matrix verification request object (can be null initially)
 * @param {string} verificationType - Type of verification ('device' or 'user')
 * @returns {Object} Verification flow state and control functions
 */
const useVerificationFlow = (request, verificationType = 'device') => {
  const { t } = useI18n();
  const [state, setState] = useState(VerificationFlowState.IDLE);
  const [emojis, setEmojis] = useState(null);
  const [error, setError] = useState(null);
  const [verifier, setVerifier] = useState(null);

  const requestRef = useRef(request);
  const verifierRef = useRef(null);
  const sasObjectRef = useRef(null);  // Store SAS object with confirm method
  const listenersRef = useRef(new Map());
  const isUnmountedRef = useRef(false);
  
  // Store handler functions in refs to avoid circular dependencies
  const handlersRef = useRef({
    setupVerifier: null,
    handleRequestChange: null,
  });

  // Update request ref when it changes
  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  /**
   * Setup verifier event listeners
   * Stored in ref to avoid circular dependency issues
   */
  const setupVerifierImpl = useCallback(async (verifierInstance) => {
    if (isUnmountedRef.current || verifierRef.current) return;

    logger.debug('useVerificationFlow: Setting up verifier');
    verifierRef.current = verifierInstance;
    setVerifier(verifierInstance);

    // Listen for SAS emojis
    const showSasHandler = (sas) => {
      if (isUnmountedRef.current) return;

      // CRITICAL: Store the entire SAS object - it has confirm/mismatch/cancel methods
      sasObjectRef.current = sas;

      // Rust crypto emits callbacks object with nested sas property
      // The structure is: { sas: { emoji: [...], decimal: [...] }, confirm, mismatch, cancel }
      const emojiData = sas?.sas?.emoji || sas?.emoji;  // Try nested first, fallback to direct
      
      logger.info('SAS emojis ready for comparison');
      
      setEmojis(emojiData || null);
      setState(VerificationFlowState.SHOWING_SAS);
    };

    // Listen for completion
    const doneHandler = () => {
      if (isUnmountedRef.current) return;
      
      logger.debug('useVerificationFlow: Verification complete');
      setState(VerificationFlowState.DONE);
      
      trustEventEmitter.emitTrustEvent(TrustEvents.VERIFICATION_COMPLETE, {
        verificationType,
      });
    };

    // Listen for cancellation
    const cancelHandler = (reason) => {
      if (isUnmountedRef.current) return;
      
      logger.debug('useVerificationFlow: Verification cancelled:', reason);
      setState(VerificationFlowState.CANCELLED);
      
      trustEventEmitter.emitTrustEvent(TrustEvents.VERIFICATION_CANCEL, {
        verificationType,
        reason,
      });
    };

    // Register event listeners
    verifierInstance.on?.('show_sas', showSasHandler);
    verifierInstance.on?.('done', doneHandler);
    verifierInstance.on?.('cancel', cancelHandler);

    // Store listeners for cleanup
    listenersRef.current.set('show_sas', showSasHandler);
    listenersRef.current.set('done', doneHandler);
    listenersRef.current.set('cancel', cancelHandler);

    // Now we need to start the SAS verification process
    // Both sides need to call verify() to begin the emoji exchange
    if (verifierInstance.verify) {
      try {
        verifierInstance.verify().catch((err) => {
          if (isUnmountedRef.current) return;
          logger.error('useVerificationFlow: verify() failed:', err);
          setError(err.message || 'Verification failed');
          setState(VerificationFlowState.ERROR);
        });
      } catch (err) {
        if (isUnmountedRef.current) return;
        logger.error('useVerificationFlow: Failed to call verify():', err);
        setError(err.message || 'Failed to start verification');
        setState(VerificationFlowState.ERROR);
      }
    }
  }, [verificationType]);

  // Store in ref for stable access
  handlersRef.current.setupVerifier = setupVerifierImpl;

  /**
   * Handle request state changes
   * Stored in ref to avoid circular dependency issues
   */
  const handleRequestChangeImpl = useCallback(() => {
    if (isUnmountedRef.current || !requestRef.current) return;

    const phase = requestRef.current.phase;
    logger.debug('useVerificationFlow: Request phase changed:', phase);

    // Handle both numeric phases (Rust crypto) and string phases (legacy)
    // Rust crypto numeric phases:
    // 0 = Unsent, 1 = Initial, 2 = Requested, 3 = Ready, 4 = Started, 5 = Cancelled, 6 = Done
    
    // Normalize phase for comparison
    const isReady = phase === 3 || phase === 'ready';
    const isStarted = phase === 4 || phase === 'started';
    const isDone = phase === 6 || phase === 'done';
    const isCancelled = phase === 5 || phase === 'cancelled';

    if (isReady) {
      setState(VerificationFlowState.WAITING);
      
      // At phase 3, check if verifier already exists (shouldn't happen but check anyway)
      if (requestRef.current.verifier && !verifierRef.current) {
        handlersRef.current.setupVerifier?.(requestRef.current.verifier);
      }
    } else if (isStarted) {
      // Verifier should be created at this point
      // The 'verifier' event listener should catch this, but also check here
      if (requestRef.current.verifier && !verifierRef.current) {
        // Call via ref to get latest version
        handlersRef.current.setupVerifier?.(requestRef.current.verifier);
      }
    } else if (isDone) {
      setState(VerificationFlowState.DONE);
    } else if (isCancelled) {
      setState(VerificationFlowState.CANCELLED);
    }
  }, []);

  // Store in ref for stable access
  handlersRef.current.handleRequestChange = handleRequestChangeImpl;

  // BUG-C1 FIX: Cleanup verifier listeners when verifier changes
  // This prevents memory leaks when verification is restarted with a new verifier
  useEffect(() => {
    // Only set up cleanup if we actually have a verifier
    // This prevents cleanup from running when verifier is initially null
    if (!verifier) return;
    
    // Return cleanup function that runs when verifier changes to a new instance
    return () => {
      if (verifierRef.current) {
        logger.debug('useVerificationFlow: Cleaning up verifier listeners (verifier changed)');
        listenersRef.current.forEach((handler, event) => {
          try {
            verifierRef.current.off?.(event, handler);
          } catch (err) {
            logger.warn('useVerificationFlow: Error removing verifier listener:', err);
          }
        });
        listenersRef.current.clear();
      }
    };
  }, [verifier]); // Runs when verifier changes

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      logger.debug('useVerificationFlow: Cleaning up on unmount');
      isUnmountedRef.current = true;
      
      // Remove verifier event listeners (if not already cleaned up by verifier effect)
      if (verifierRef.current) {
        listenersRef.current.forEach((handler, event) => {
          try {
            verifierRef.current.off?.(event, handler);
          } catch (err) {
            logger.warn('useVerificationFlow: Error removing verifier listener:', err);
          }
        });
        listenersRef.current.clear();
      }

      // Remove request change listener
      if (requestRef.current && handlersRef.current?.handleRequestChange) {
        try {
          const changeHandler = handlersRef.current.handleRequestChange;
          requestRef.current.off?.('change', changeHandler);
        } catch (err) {
          logger.warn('useVerificationFlow: Error removing request listener:', err);
        }
      }

      // Clear refs
      verifierRef.current = null;
      sasObjectRef.current = null;
      handlersRef.current = { setupVerifier: null, handleRequestChange: null };
    };
  }, []); // Empty deps OK - uses refs for everything

  /**
   * Accept incoming verification request
   */
  const acceptRequest = useCallback(async () => {
    if (!requestRef.current) {
      logger.error('useVerificationFlow: No request to accept');
      return false;
    }

    try {
      logger.info('Responder accepted verification request');
      setState(VerificationFlowState.ACCEPTING);
      setError(null);
      
      await requestRef.current.accept();

      // Remove old listener if exists (prevent duplicates)
      if (handlersRef.current.handleRequestChange) {
        requestRef.current.off?.('change', handlersRef.current.handleRequestChange);
      }

      // Listen for request changes using stable ref
      requestRef.current.on?.('change', handlersRef.current.handleRequestChange);

      // Listen for verifier creation
      const verifierHandler = (v) => {
        if (!isUnmountedRef.current && v && !verifierRef.current) {
          handlersRef.current.setupVerifier?.(v);
        }
      };
      requestRef.current.on?.('verifier', verifierHandler);

      // Check if verifier already exists
      if (requestRef.current.verifier) {
        handlersRef.current.setupVerifier?.(requestRef.current.verifier);
      } else {
        // Wait for verifier
        setState(VerificationFlowState.WAITING);
      }

      return true;
    } catch (err) {
      if (isUnmountedRef.current) return false;
      logger.error('useVerificationFlow: Failed to accept request:', err);
      setError(err.message || 'Failed to accept verification request');
      setState(VerificationFlowState.ERROR);
      
      trustEventEmitter.emitTrustEvent(TrustEvents.VERIFICATION_ERROR, {
        verificationType,
        error: err.message,
      });
      
      return false;
    }
  }, [verificationType]);

  /**
   * Reject incoming verification request
   */
  const rejectRequest = useCallback(async (reason = 'user') => {
    if (!requestRef.current) {
      logger.error('useVerificationFlow: No request to reject');
      return false;
    }

    try {
      logger.debug('useVerificationFlow: Rejecting verification request');
      await requestRef.current.cancel?.(reason);
      
      setState(VerificationFlowState.CANCELLED);
      
      trustEventEmitter.emitTrustEvent(TrustEvents.VERIFICATION_CANCEL, {
        verificationType,
        reason,
      });
      
      return true;
    } catch (err) {
      if (isUnmountedRef.current) return false;
      logger.error('useVerificationFlow: Failed to reject request:', err);
      setError(err.message || 'Failed to reject verification request');
      return false;
    }
  }, [verificationType]);

  /**
   * Confirm SAS match
   */
  const confirmMatch = useCallback(async () => {
    if (!sasObjectRef.current) {
      logger.error('useVerificationFlow: No SAS object to confirm');
      return false;
    }

    if (state !== VerificationFlowState.SHOWING_SAS) {
      logger.error('useVerificationFlow: Cannot confirm in current state:', state);
      return false;
    }

    try {
      logger.info('User confirmed SAS match');
      setState(VerificationFlowState.VERIFYING);
      setError(null);

      await sasObjectRef.current.confirm();

      return true;
    } catch (err) {
      if (isUnmountedRef.current) return false;
      logger.error('useVerificationFlow: Failed to confirm match:', err);
      setError(err.message || 'Failed to confirm match');
      setState(VerificationFlowState.ERROR);

      trustEventEmitter.emitTrustEvent(TrustEvents.VERIFICATION_ERROR, {
        verificationType,
        error: err.message,
      });

      return false;
    }
  }, [state, verificationType]);

  /**
   * Reject SAS match (mismatch)
   */
  const rejectMatch = useCallback(async () => {
    if (!sasObjectRef.current) {
      logger.error('useVerificationFlow: No SAS object to reject');
      return false;
    }

    try {
      logger.debug('useVerificationFlow: Rejecting SAS match (mismatch)');
      await sasObjectRef.current.mismatch();

      if (isUnmountedRef.current) return true;
      setState(VerificationFlowState.CANCELLED);
      setError(getVerificationErrorMessage('m.mismatched_sas', t));

      trustEventEmitter.emitTrustEvent(TrustEvents.VERIFICATION_CANCEL, {
        verificationType,
        reason: 'm.mismatched_sas',
      });

      return true;
    } catch (err) {
      if (isUnmountedRef.current) return false;
      logger.error('useVerificationFlow: Failed to reject match:', err);
      setError(err.message || getVerificationErrorMessage('default', t));
      return false;
    }
  }, [verificationType, t]);

  /**
   * Cancel verification
   */
  const cancel = useCallback(async (reason = 'user') => {
    try {
      logger.debug('useVerificationFlow: Cancelling verification');

      if (verifierRef.current) {
        await verifierRef.current.cancel?.(reason);
      } else if (requestRef.current) {
        await requestRef.current.cancel?.(reason);
      }

      if (isUnmountedRef.current) return;
      setState(VerificationFlowState.CANCELLED);
      
      trustEventEmitter.emitTrustEvent(TrustEvents.VERIFICATION_CANCEL, {
        verificationType,
        reason,
      });
    } catch (err) {
      if (isUnmountedRef.current) return;
      logger.error('useVerificationFlow: Failed to cancel:', err);
      setError(err.message || 'Failed to cancel verification');
    }
  }, [verificationType]);

  /**
   * Initialize verification from request
   * CRITICAL: This handles pre-accepted requests (phase >= 3) from VerificationCoordinator
   *
   * Note: We only depend on [request], not [state], to avoid cleanup when state changes internally.
   * The state check ensures this only runs once when moving from IDLE to another state.
   */
  useEffect(() => {
    if (!request || state !== VerificationFlowState.IDLE) {
      return;
    }

    // Set up change listener IMMEDIATELY for all requests
    // This is critical for pre-accepted requests where acceptRequest() won't be called
    requestRef.current.on?.('change', handlersRef.current.handleRequestChange);

    // Set up verifier listener (use 'on' not 'once' to ensure it triggers)
    const verifierHandler = (v) => {
      if (!isUnmountedRef.current && v && !verifierRef.current) {
        handlersRef.current.setupVerifier?.(v);
      }
    };
    requestRef.current.on?.('verifier', verifierHandler);

    // Check current state of request
    const currentPhase = request.phase;

    if (request.verifier && !verifierRef.current) {
      // Verifier already exists, set up immediately
      handlersRef.current.setupVerifier?.(request.verifier);
    } else if (currentPhase >= 3) {
      // Request already accepted (phase 3 = READY, 4 = STARTED)
      if (currentPhase === 4 && request.verifier) {
        // Already started with verifier
        handlersRef.current.setupVerifier?.(request.verifier);
      } else {
        // Waiting for initiator to start (phase 3 with no verifier)
        setState(VerificationFlowState.WAITING);
      }
    }

    // Cleanup - only runs when request changes or component unmounts
    // NOT when state changes (we removed state from dependencies)
    return () => {
      // Remove change listener - must extract function from ref
      const changeHandler = handlersRef.current?.handleRequestChange;
      if (changeHandler) {
        requestRef.current?.off?.('change', changeHandler);
      }
      
      // Remove verifier listener
      requestRef.current?.off?.('verifier', verifierHandler);
    };
  }, [request]); // Only depend on request, not state!

  return {
    state,
    emojis,
    error,
    verifier,
    acceptRequest,
    rejectRequest,
    confirmMatch,
    rejectMatch,
    cancel,
  };
};

export default useVerificationFlow;
